import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useKeycloak} from '@react-keycloak/web';
import {useIntl} from 'react-intl';

import {Col, Form, Input, message, Row, Select} from 'antd';
import {findCountries} from 'services/CountryService';
import {findWards} from 'services/WardService';
import {findDistricts} from 'services/DistrictService';
import {findProvinces} from 'services/ProvinceService';
import {QUERY_SIZE_LIMIT} from 'shared/constants/AppConst';
import {handleError} from 'utils/MessageUtils';

const Address = (props) => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const {address, path} = props;
  const [districtId, setDistrictId] = useState();
  const [provinceId, setProvinceId] = useState();
  const [countryId, setCountryId] = useState();

  const [wards, setWards] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    if (countries.length === 0) {
      findCountries(headers, {
        filters: [
          {
            fields: 'enabled',
            value: true,
          },
        ],
        pagination: {current: 1, pageSize: QUERY_SIZE_LIMIT},
      })
        .then((res) => setCountries(res.data))
        .catch((error) => handleError(message, error));
    }

    if (address?.country?.id && countryId !== address.country.id) {
      fetchProvinces(address.country.id);
      setCountryId(address.country.id);
    }

    if (address?.province?.id && provinceId !== address.province.id) {
      fetchDistricts(address.province.id);
      setProvinceId(address.province.id);
    }

    if (address?.district?.id && districtId !== address.district.id) {
      fetchWards(address.district.id);
      setDistrictId(address.district.id);
    }
  }, [address]);

  const fetchWards = (districtId) => {
    const filters = [
      {
        fields: 'enabled',
        value: true,
      },
    ];

    if (districtId) {
      filters.push({
        fields: 'district.code',
        value: districtId,
      });
    }

    findWards(headers, {
      filters,
      pagination: {current: 1, pageSize: QUERY_SIZE_LIMIT},
    })
      .then((res) => setWards(res.data))
      .catch((error) => handleError(message, error));
  };

  const fetchDistricts = (provinceId) => {
    const filters = [
      {
        fields: 'enabled',
        value: true,
      },
    ];

    if (provinceId) {
      filters.push({
        fields: 'province.code',
        value: provinceId,
      });
    }

    findDistricts(headers, {
      filters,
      pagination: {current: 1, pageSize: QUERY_SIZE_LIMIT},
    })
      .then((res) => setDistricts(res.data))
      .catch((error) => handleError(message, error));
  };

  const fetchProvinces = (countryId) => {
    const filters = [
      {
        fields: 'enabled',
        value: true,
      },
    ];

    if (countryId) {
      filters.push({
        fields: 'country.code',
        value: countryId,
      });
    }

    findProvinces(headers, {
      filters,
      pagination: {current: 1, pageSize: QUERY_SIZE_LIMIT},
    })
      .then((res) => setProvinces(res.data))
      .catch((error) => handleError(message, error));
  };

  const onCountryChange = (countryId) => {
    fetchProvinces(countryId);
  };

  const onProvinceChange = (provinceId) => {
    fetchDistricts(provinceId);
  };

  const onDistrictChange = (districtId) => {
    fetchWards(districtId);
  };

  return (
    <>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name={[...path, 'line']}
            label={intl.messages['address.line']}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item
            name={[...path, 'country', 'id']}
            label={intl.messages['address.country']}>
            <Select showSearch={true} onChange={onCountryChange}>
              {countries.map((country, index) => (
                <Select.Option value={country.code} key={index}>
                  {country.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={[...path, 'province', 'id']}
            label={intl.messages['address.province']}>
            <Select onChange={onProvinceChange}>
              {provinces.map((province, index) => (
                <Select.Option value={province.code} key={index}>
                  {province.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={[...path, 'district', 'id']}
            label={intl.messages['address.district']}>
            <Select onChange={onDistrictChange}>
              {districts.map((district, index) => (
                <Select.Option value={district.code} key={index}>
                  {district.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={[...path, 'ward', 'id']}
            label={intl.messages['address.ward']}>
            <Select>
              {wards.map((ward, index) => (
                <Select.Option value={ward.code} key={index}>
                  {ward.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default Address;

Address.propTypes = {
  address: PropTypes.object,
  path: PropTypes.array.isRequired,
};
